import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material/dialog';

import { AssetManagement } from '@aptsi-types';
import { SystemAssetSourceService } from '../services/system-asset-source.service';

interface FileImportDialogData {
  assetSourceId: string;
  assetSourceName: string;
  assetSourceType: string;
  assetSource: string;
}

interface FileFormatConfig {
  label: string;
  accept: string;
  extensions: string[];
}

@Component({
  selector: 'app-system-assets-capture-for-file-asset-source',
  templateUrl: './system-assets-capture-for-file-asset-source.component.html',
  styleUrls: ['./system-assets-capture-for-file-asset-source.component.scss']
})
export class SystemAssetsCaptureForFileAssetSourceComponent {
  selectedFile: File | null = null;

  uploading = false;
  fetchingAssets = false;
  assetsLoaded = false;

  importMessage = '';
  errorMessage = '';

  assets: AssetManagement[] = [];

  /**
   * Add new formats here as support expands.
   *
   * The key should match the assetSource value stored in the database.
   */
  private readonly supportedFormats: Record<string, FileFormatConfig> = {
    csv: {
      label: 'CSV file',
      accept: '.csv,text/csv',
      extensions: ['csv']
    },
    excel: {
      label: 'Excel spreadsheet',
      accept: '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
      extensions: ['xlsx', 'xls']
    },
    xlsx: {
      label: 'Excel spreadsheet',
      accept: '.xlsx',
      extensions: ['xlsx']
    },
    xls: {
      label: 'Excel spreadsheet',
      accept: '.xls',
      extensions: ['xls']
    },
    pdf: {
      label: 'PDF document',
      accept: '.pdf,application/pdf',
      extensions: ['pdf']
    },
    'application/pdf': {
      label: 'PDF document',
      accept: '.pdf,application/pdf',
      extensions: ['pdf']
    },
    json: {
      label: 'JSON file',
      accept: '.json,application/json',
      extensions: ['json']
    },
    txt: {
      label: 'Text file',
      accept: '.txt,text/plain',
      extensions: ['txt']
    },
    xml: {
      label: 'XML file',
      accept: '.xml,application/xml,text/xml',
      extensions: ['xml']
    },
    doc: {
      label: 'Word document',
      accept: '.doc,.docx',
      extensions: ['doc', 'docx']
    },
    docx: {
      label: 'Word document',
      accept: '.doc,.docx',
      extensions: ['doc', 'docx']
    },
    ppt: {
      label: 'PowerPoint presentation',
      accept: '.ppt,.pptx',
      extensions: ['ppt', 'pptx']
    },
    pptx: {
      label: 'PowerPoint presentation',
      accept: '.ppt,.pptx',
      extensions: ['ppt', 'pptx']
    }
  };

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: FileImportDialogData,
    private readonly dialogRef: MatDialogRef<SystemAssetsCaptureForFileAssetSourceComponent>,
    private readonly systemAssetSourceService: SystemAssetSourceService
  ) {}

  get formatConfig(): FileFormatConfig {
    const format = String(this.data.assetSource ?? '')
      .trim()
      .toLowerCase()
      .replace(/^\./, '');

    const knownFormat = this.supportedFormats[format];

    if (knownFormat) {
      return knownFormat;
    }

    /*
     * Future-proof fallback:
     * If assetSource contains a simple extension, use it directly.
     * For example, a future assetSource value of "yaml" accepts .yaml.
     */
    if (/^[a-z0-9]{1,12}$/.test(format)) {
      return {
        label: `${format.toUpperCase()} file`,
        accept: `.${format}`,
        extensions: [format]
      };
    }

    // If the format is not specified, allow file selection without
    // applying a format-specific extension restriction.
    return {
      label: 'file',
      accept: '',
      extensions: []
    };
  }

  get acceptedFileTypes(): string {
    return this.formatConfig.accept;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    this.assignFile(file);

    // Allow the user to select the same file again.
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files?.[0] ?? null;
    this.assignFile(file);
  }

  removeSelectedFile(): void {
    this.selectedFile = null;
    this.errorMessage = '';
    this.importMessage = '';
  }

  uploadAndFetchAssets(): void {
    if (
      !this.selectedFile ||
      !this.data.assetSourceId ||
      this.uploading ||
      this.fetchingAssets
    ) {
      return;
    }

    this.uploading = true;
    this.fetchingAssets = false;
    this.assetsLoaded = false;
    this.assets = [];
    this.errorMessage = '';
    this.importMessage = '';

    // Step 1: Upload and import the selected file.
    this.systemAssetSourceService
      .ImportSystemAssetsFromFile(
        this.selectedFile,
        this.data.assetSourceId
      )
      .subscribe({
        next: response => {
          this.importMessage =
            this.extractMessage(response) ||
            'File imported successfully. Fetching system assets...';

          // Step 2: Fetch all assets associated with this asset source.
          this.fetchingAssets = true;

          this.systemAssetSourceService
            .FetchSystemAssetsFromFile(this.data.assetSourceId)
            .subscribe({
              next: response => {
                this.assets = this.normalizeAssets(response);
                this.assetsLoaded = true;
                this.fetchingAssets = false;
                this.uploading = false;

                this.importMessage =
                  `Import completed. ${this.assets.length} system asset(s) fetched.`;
              },
              error: error => {
                this.fetchingAssets = false;
                this.uploading = false;

                this.errorMessage =
                  this.extractMessage(error) ||
                  'The file was imported, but the system assets could not be fetched.';
              }
            });
        },
        error: error => {
          this.uploading = false;
          this.fetchingAssets = false;

          this.errorMessage =
            this.extractMessage(error) ||
            'Unable to import the selected file. Check the file format and try again.';
        }
      });
  }

  formatFileSize(size: number): string {
    if (!Number.isFinite(size) || size < 0) {
      return '—';
    }

    if (size < 1024) {
      return `${size} B`;
    }

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  close(): void {
    if (this.uploading || this.fetchingAssets) {
      return;
    }

    this.dialogRef.close({
      imported: this.assetsLoaded
    });
  }

  private assignFile(file: File | null): void {
    this.errorMessage = '';
    this.importMessage = '';
    this.assets = [];
    this.assetsLoaded = false;

    if (!file) {
      this.selectedFile = null;
      return;
    }

    if (!this.isSupportedFile(file)) {
      this.selectedFile = null;

      this.errorMessage = this.formatConfig.extensions.length
        ? `Unsupported file type. Please select a ${this.formatConfig.label}.`
        : 'Unable to determine the selected file format.';

      return;
    }

    this.selectedFile = file;
  }

  private isSupportedFile(file: File): boolean {
    const extensions = this.formatConfig.extensions;

    // An empty extension list means no format-specific restriction.
    if (extensions.length === 0) {
      return true;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

    return extensions.includes(extension);
  }

  /**
   * The backend may return an array directly or wrap the records
   * in an object. Normalize common response shapes for display.
   */
  private normalizeAssets(response: unknown): AssetManagement[] {
    if (Array.isArray(response)) {
      return response as AssetManagement[];
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const payload = response as Record<string, unknown>;

    const possibleArrays = [
      'systemAssets',
      'assets',
      'data',
      'results',
      'assetList',
      'systemAssetList',
      'systemAssetsList',
      'systemAssetDTOs',
      'assetManagements'
    ];

    for (const key of possibleArrays) {
      if (Array.isArray(payload[key])) {
        return payload[key] as AssetManagement[];
      }
    }

    // Accommodate an API that returns a single asset object.
    const assetFields = [
      'systemAssetId',
      'systemAssetName',
      'assetId',
      'assetName',
      'uniqueIdentifier'
    ];

    if (assetFields.some(key => payload[key] !== undefined)) {
      return [payload as AssetManagement];
    }

    return [];
  }

  private extractMessage(responseOrError: unknown): string {
    if (typeof responseOrError === 'string') {
      return responseOrError;
    }

    if (!responseOrError || typeof responseOrError !== 'object') {
      return '';
    }

    const value = responseOrError as Record<string, unknown>;

    const candidates = [
      value['error'],
      value['message']
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }

      if (candidate && typeof candidate === 'object') {
        const message = Object.values(candidate).find(
          item => typeof item === 'string' && item.trim()
        );

        if (typeof message === 'string') {
          return message;
        }
      }
    }

    return '';
  }
}